import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Repeat, ArrowsClockwise, Trash, Plus, Play, Pause } from "@phosphor-icons/react";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // Python weekday() 0=Mon
const NTH = ["1st", "2nd", "3rd", "4th", "5th"];

export default function AdminScheduleRules() {
  const [rules, setRules] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/schedule-rules"),
      api.get("/tours"),
    ]).then(([r, t]) => {
      setRules(r.data || []);
      setTours(t.data || []);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (rule) => {
    const deleteInstances = window.confirm(
      `Delete this rule?\n\nClick OK to also delete future un-booked instances generated by this rule.\nClick Cancel to delete only the rule and keep the instances.`
    );
    try {
      await api.delete(`/admin/schedule-rules/${rule.id}`, { params: { delete_future_instances: deleteInstances } });
      load();
    } catch (e) {
      // Cancel = keep instances
      try { await api.delete(`/admin/schedule-rules/${rule.id}`); load(); } catch {}
    }
  };
  const toggle = async (rule) => {
    const newStatus = rule.status === "active" ? "paused" : "active";
    await api.patch(`/admin/schedule-rules/${rule.id}`, { status: newStatus });
    load();
  };
  const runNow = async (rule) => {
    const r = await api.post(`/admin/schedule-rules/${rule.id}/run-now`);
    alert(`Generated ${r.data.instances_created} instance(s).`);
    load();
  };

  const describe = (r) => {
    if (r.cadence === "weekly") return `Every ${WEEKDAYS[r.weekday]} at ${r.time_of_day}`;
    return `${NTH[r.monthly_nth - 1]} ${WEEKDAYS[r.weekday]} of each month at ${r.time_of_day}`;
  };

  return (
    <div className="p-10 md:p-12" data-testid="admin-schedule-rules">
      <p className="label-eyebrow text-ms-stone">Bookings</p>
      <h1 className="serif text-4xl text-ms-earth mt-2 flex items-center gap-3">
        <Repeat size={32} weight="duotone" className="text-ms-brass" /> Recurring schedule rules
      </h1>
      <p className="mt-2 text-sm text-ms-stone max-w-2xl">
        Set a weekly or monthly cadence (e.g. "every Saturday at 6 PM") to auto-generate tour dates through an end-date. Rules regenerate hourly. Inventory and pricing follow the tour template.
      </p>

      <div className="mt-4 flex items-center gap-2">
        <button onClick={() => setShowNew(true)} className="btn-primary inline-flex items-center gap-2" data-testid="sr-add-rule">
          <Plus size={14} /> Add a rule
        </button>
        <button onClick={load} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-ms-edge rounded-md text-ms-stone hover:bg-ms-cream" data-testid="sr-refresh">
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>

      {showNew && <NewRule tours={tours} onClose={() => { setShowNew(false); load(); }} />}

      <section className="mt-8 bg-white border border-ms-edge rounded-2xl overflow-hidden">
        {loading ? (
          <p className="p-6 text-sm text-ms-stone">Loading…</p>
        ) : rules.length === 0 ? (
          <p className="p-6 text-sm text-ms-stone">No recurring rules yet. Click "Add a rule" to set one up.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-ms-cream/40 border-b border-ms-edge">
              <tr>
                <th className="text-left p-4 label-eyebrow text-[10px]">Tour</th>
                <th className="text-left p-4 label-eyebrow text-[10px]">Cadence</th>
                <th className="text-right p-4 label-eyebrow text-[10px]">Capacity</th>
                <th className="text-left p-4 label-eyebrow text-[10px]">Through</th>
                <th className="text-left p-4 label-eyebrow text-[10px]">Status</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {rules.map(r => (
                <tr key={r.id} className="border-b border-ms-edge last:border-0" data-testid={`sr-row-${r.id}`}>
                  <td className="p-4 text-ms-earth">{r.tour_title || r.tour_id}</td>
                  <td className="p-4 text-ms-stone">{describe(r)}</td>
                  <td className="p-4 text-right text-ms-earth">{r.capacity}</td>
                  <td className="p-4 text-ms-stone">{r.end_date}</td>
                  <td className="p-4">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${r.status === "active" ? "bg-ms-success/10 text-ms-success" : "bg-ms-edge text-ms-stone"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="p-4 text-right space-x-2">
                    <button onClick={() => runNow(r)} className="text-xs label-eyebrow text-ms-brass hover:text-ms-brass-lo" data-testid={`sr-run-${r.id}`}>Run now</button>
                    <button onClick={() => toggle(r)} className="text-xs label-eyebrow text-ms-stone hover:text-ms-earth" data-testid={`sr-toggle-${r.id}`}>
                      {r.status === "active" ? <Pause size={14} className="inline" /> : <Play size={14} className="inline" />}
                    </button>
                    <button onClick={() => remove(r)} className="text-ms-danger hover:text-ms-danger/70" data-testid={`sr-remove-${r.id}`}>
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function NewRule({ tours, onClose }) {
  const [form, setForm] = useState({
    tour_id: tours[0]?.id || "",
    cadence: "weekly",
    weekday: 5, // Saturday
    monthly_nth: 1,
    time_of_day: "18:00",
    capacity: 12,
    end_date: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.tour_id || !form.end_date) { setError("Tour and end date are required."); return; }
    setSaving(true);
    try {
      const payload = { ...form, capacity: Number(form.capacity), weekday: Number(form.weekday), monthly_nth: Number(form.monthly_nth) };
      if (form.cadence === "weekly") delete payload.monthly_nth;
      const r = await api.post("/admin/schedule-rules", payload);
      alert(`Rule created. Generated ${r.data.instances_created || 0} initial instance(s).`);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not create rule.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="sr-new-modal">
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white border border-ms-edge rounded-md max-w-xl w-full p-6 space-y-4">
        <h2 className="serif text-2xl text-ms-earth">Add a recurring rule</h2>

        <label className="block">
          <span className="label-eyebrow text-ms-stone text-[10px]">Tour</span>
          <select value={form.tour_id} onChange={e => upd("tour_id", e.target.value)} className="mt-1 w-full border border-ms-edge rounded-md px-3 py-2 text-sm" data-testid="sr-form-tour">
            {tours.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="label-eyebrow text-ms-stone text-[10px]">Cadence</span>
            <select value={form.cadence} onChange={e => upd("cadence", e.target.value)} className="mt-1 w-full border border-ms-edge rounded-md px-3 py-2 text-sm" data-testid="sr-form-cadence">
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </label>
          {form.cadence === "monthly" && (
            <label className="block">
              <span className="label-eyebrow text-ms-stone text-[10px]">Occurrence</span>
              <select value={form.monthly_nth} onChange={e => upd("monthly_nth", e.target.value)} className="mt-1 w-full border border-ms-edge rounded-md px-3 py-2 text-sm" data-testid="sr-form-nth">
                {NTH.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
              </select>
            </label>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="label-eyebrow text-ms-stone text-[10px]">Weekday</span>
            <select value={form.weekday} onChange={e => upd("weekday", e.target.value)} className="mt-1 w-full border border-ms-edge rounded-md px-3 py-2 text-sm" data-testid="sr-form-weekday">
              {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="label-eyebrow text-ms-stone text-[10px]">Time (24h, local)</span>
            <input type="time" value={form.time_of_day} onChange={e => upd("time_of_day", e.target.value)} className="mt-1 w-full border border-ms-edge rounded-md px-3 py-2 text-sm" data-testid="sr-form-time" />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="label-eyebrow text-ms-stone text-[10px]">Capacity per date</span>
            <input type="number" min="1" max="200" value={form.capacity} onChange={e => upd("capacity", e.target.value)} className="mt-1 w-full border border-ms-edge rounded-md px-3 py-2 text-sm" data-testid="sr-form-capacity" />
          </label>
          <label className="block">
            <span className="label-eyebrow text-ms-stone text-[10px]">Generate through (end date)</span>
            <input type="date" value={form.end_date} onChange={e => upd("end_date", e.target.value)} className="mt-1 w-full border border-ms-edge rounded-md px-3 py-2 text-sm" data-testid="sr-form-end" />
          </label>
        </div>

        {error && <p className="text-ms-danger text-sm" data-testid="sr-form-error">{error}</p>}
        <div className="flex justify-end gap-2 pt-2 border-t border-ms-edge">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="sr-form-submit">
            {saving ? "Creating…" : "Create rule & generate dates"}
          </button>
        </div>
      </form>
    </div>
  );
}
